import { useEffect, useState } from 'react'

export interface ServerReadinessState {
  isServerReady: boolean
  isChecking: boolean
  error: string | null
}

const HEALTHCHECK_INTERVAL = 1000 // Check every 1 second
const MAX_RETRIES = 30 // Give up after 30 seconds

export function useServerReadiness(serverUrl: string): ServerReadinessState {
  const [isServerReady, setIsServerReady] = useState(false)
  const [isChecking, setIsChecking] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    let intervalId: NodeJS.Timeout
    let timeoutId: NodeJS.Timeout

    const checkServerHealth = async () => {
      try {
        const response = await fetch(`${serverUrl}/api/healthcheck`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })

        if (response.ok) {
          const data = await response.json()
          if (data.status === 'healthy') {
            setIsServerReady(true)
            setIsChecking(false)
            setError(null)
            if (intervalId) clearInterval(intervalId)
            if (timeoutId) clearTimeout(timeoutId)
            return
          }
        }

        // Server responded but not healthy yet
        setRetryCount(prev => prev + 1)
      } catch (err) {
        // Server not responding yet, increment retry count
        setRetryCount(prev => prev + 1)
        console.log(
          `Server healthcheck failed (attempt ${retryCount + 1}/${MAX_RETRIES}):`,
          err,
        )
      }
    }

    // Start checking immediately
    checkServerHealth()

    // Set up interval to check repeatedly
    intervalId = setInterval(checkServerHealth, HEALTHCHECK_INTERVAL)

    // Set up timeout to give up after MAX_RETRIES
    timeoutId = setTimeout(() => {
      if (intervalId) clearInterval(intervalId)
      setIsChecking(false)
      setError(
        'Server failed to start within expected time. Please try restarting the application.',
      )
    }, MAX_RETRIES * HEALTHCHECK_INTERVAL)

    return () => {
      if (intervalId) clearInterval(intervalId)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [serverUrl, retryCount])

  return { isServerReady, isChecking, error }
}
